import type {
  ApiResponse,
  PaginatedResponse,
  PlayEvent,
  PlayEventCreateInput,
  PlayEventFormat,
  PlayEventMatch,
  PlayEventParticipant,
  PlayEventParticipantAddInput,
  PlayEventParticipantUpdateInput,
  PlayEventRound,
  PlayEventSeries,
  PlayEventTeam,
  PlayEventSeriesCreateInput,
  PlayEventSeriesUpdateInput,
  PlayEventUpdateInput,
  PlayEventDrawGenerateInput,
  PlayEventRoundAssignInput,
  PlayEventRoundRemoveInput,
  PublicPlayEventParticipant,
} from '@court-sync/shared';
import type { AxiosRequestConfig } from 'axios';
import { apiGet, apiPatch, apiPost, apiDelete, publicApiGet, toApiError } from './apiClient';

export interface PlayEventScoreInput {
  result: 'winner' | 'walkover' | 'retired' | 'bye' | 'cancelled';
  winnerSide?: 'a' | 'b';
  score?: {
    sets?: Array<{ a: number; b: number }>;
    games?: Array<{ a: number; b: number }>;
  };
}

export interface PlayEventSocialWithdrawInput {
  roundNumber: number;
}

export interface MyPlayEventRegistration {
  event: PublicPlayEventSummary;
  participantId: string;
  participantType: 'member' | 'student' | 'guest';
  participantStatus: 'registered' | 'checked_in' | 'withdrawn' | 'no_show';
  paymentStatus: 'unpaid' | 'paid' | 'comped' | 'waived';
}

export interface PlayEventPublicParticipantSummary {
  total: number;
  checkedIn: number;
}

export interface PlayEventOperatorDetailResponse {
  event: PlayEvent;
  participantSummary: PlayEventParticipantSummary;
  participants: PlayEventParticipant[];
  rounds: PlayEventRound[];
  matches: PlayEventMatch[];
  teams: PlayEventTeam[];
}

export interface PlayEventPublicDetailResponse {
  event: PlayEvent;
  participantSummary: PlayEventPublicParticipantSummary;
  participants: PublicPlayEventParticipant[];
  rounds: PlayEventRound[];
  matches: PlayEventMatch[];
  teams: PlayEventTeam[];
}

/** @deprecated Use PlayEventOperatorDetailResponse or PlayEventPublicDetailResponse */
export type PlayEventDetailResponse = PlayEventOperatorDetailResponse;

export interface PlayEventParticipantSummary {
  total: number;
  checkedIn: number;
  unpaidPriced: number;
  registeredMemberIds: string[];
  registeredStudentIds: string[];
}

export interface PlayEventPublicBoardBracket {
  matches: Array<{
    roundNumber: number;
    matchIndex: number;
    sideAId?: string | null;
    sideBId?: string | null;
    isBye?: boolean;
  }>;
  participantNamesById: Record<string, string>;
  isPreview: boolean;
}

export interface PlayEventPublicBoardResponse {
  event: Pick<
    PlayEvent,
    | 'id'
    | 'name'
    | 'slug'
    | 'status'
    | 'startsAt'
    | 'endsAt'
    | 'matchupVisibilityMinutes'
    | 'format'
    | 'roundCount'
    | 'roundDurationMinutes'
    | 'roundBreakMinutes'
  >;
  matches: PlayEventMatch[];
  rounds: PlayEventRound[];
  teams: PlayEventTeam[];
  participants: PublicPlayEventParticipant[];
  courtNamesById: Record<string, string>;
  drawStarted: boolean;
  nextReveal: { roundNumber: number; revealAt: string | Date } | null;
  sittingOutByRound: Record<number, PublicPlayEventParticipant[]>;
  bracket: PlayEventPublicBoardBracket | null;
}

export interface PlayEventJoinPayload {
  participantType: 'member' | 'student' | 'guest';
  memberId?: string;
  studentId?: string;
  guestFirstName?: string;
  guestLastName?: string;
  contactEmail?: string;
  contactPhone?: string;
  gender?: 'male' | 'female' | 'other';
  skillLevel?: string;
}

export interface PlayEventJoinResponse {
  participantId: string;
  paymentRequired?: boolean;
  finalPriceCents?: number;
  clientSecret?: string | null;
  paymentIntentId?: string | null;
  stripeConnectAccountId?: string | null;
  totalCents?: number;
  currency?: string;
}

export interface AddParticipantResult {
  participantId: string;
  paymentRequired: boolean;
  finalPriceCents: number;
  clientSecret?: string;
  paymentIntentId?: string;
  stripeConnectAccountId?: string;
}

export interface PublicPlayEventSummary extends PlayEvent {
  participantCount?: number;
  spotsRemaining?: number | null;
  isRecurring?: boolean;
  recurrenceLabel?: string | null;
}

function operatorBase(orgId: string): string {
  return `/play-events/${orgId}`;
}

/** Avoid 304 Not Modified responses with empty bodies breaking operator detail aggregation. */
const OPERATOR_DETAIL_GET_CONFIG: AxiosRequestConfig = {
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
};

async function safeApiGet<T>(
  url: string,
  config: AxiosRequestConfig = OPERATOR_DETAIL_GET_CONFIG
): Promise<ApiResponse<T>> {
  try {
    return await apiGet<T>(url, config);
  } catch (error) {
    const apiError = toApiError(error);
    return { success: false, error: apiError.message };
  }
}

function publicBase(orgSlug: string): string {
  return `/public/play-events/${encodeURIComponent(orgSlug)}`;
}

async function fetchOperatorDetail(
  orgId: string,
  eventId: string
): Promise<ApiResponse<PlayEventOperatorDetailResponse>> {
  const base = `${operatorBase(orgId)}/events/${eventId}`;
  const [eventRes, summaryRes, participantsRes, roundsRes, matchesRes, teamsRes] =
    await Promise.all([
      safeApiGet<PlayEvent>(base),
      safeApiGet<PlayEventParticipantSummary>(`${base}/participants/summary`),
      safeApiGet<PlayEventParticipant[]>(`${base}/participants`),
      safeApiGet<PlayEventRound[]>(`${base}/rounds`),
      safeApiGet<PlayEventMatch[]>(`${base}/matches`),
      safeApiGet<PlayEventTeam[]>(`${base}/teams`),
    ]);

  if (!eventRes.success || !eventRes.data) {
    return { success: false, error: eventRes.error ?? 'Failed to load event' };
  }

  const participantSummary =
    summaryRes.success && summaryRes.data
      ? summaryRes.data
      : {
          total: 0,
          checkedIn: 0,
          unpaidPriced: 0,
          registeredMemberIds: [],
          registeredStudentIds: [],
        };

  return {
    success: true,
    data: {
      event: eventRes.data,
      participantSummary,
      participants: participantsRes.success && participantsRes.data ? participantsRes.data : [],
      rounds: roundsRes.success && roundsRes.data ? roundsRes.data : [],
      matches: matchesRes.success && matchesRes.data ? matchesRes.data : [],
      teams: teamsRes.success && teamsRes.data ? teamsRes.data : [],
    },
  };
}

export const playEventService = {
  // ─── Operator (organization-scoped) ─────────────────────────────────────

  async listEvents(
    orgId: string,
    filters?: {
      status?: PlayEvent['status'] | 'all';
      from?: string;
      to?: string;
      seriesId?: string;
      search?: string;
      page?: number;
      limit?: number;
      allUpcoming?: boolean;
      standaloneOnly?: boolean;
      /** Calendar layer: include materialized sessions from recurring series. */
      includeSeriesEvents?: boolean;
      startsAtOrder?: 'asc' | 'desc';
    }
  ): Promise<ApiResponse<PlayEvent[]> | PaginatedResponse<PlayEvent>> {
    const params = { ...filters } as Record<string, unknown>;
    if (params.includeSeriesEvents) {
      params.includeSeriesEvents = 'true';
    }
    if (params.allUpcoming) {
      delete params.allUpcoming;
      const from = new Date();
      const to = new Date(from);
      to.setFullYear(to.getFullYear() + 2);
      params.from = from.toISOString();
      params.to = to.toISOString();
    }
    return apiGet<PlayEvent[]>(`${operatorBase(orgId)}/events`, { params });
  },

  async createEvent(
    orgId: string,
    data: PlayEventCreateInput
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`${operatorBase(orgId)}/events`, data);
  },

  async getEvent(orgId: string, eventId: string): Promise<ApiResponse<PlayEventOperatorDetailResponse>> {
    return fetchOperatorDetail(orgId, eventId);
  },

  async updateEvent(
    orgId: string,
    eventId: string,
    data: PlayEventUpdateInput
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`${operatorBase(orgId)}/events/${eventId}`, data);
  },

  async publishEvent(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/publish`);
  },

  async unpublishEvent(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/unpublish`);
  },

  async cancelEvent(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/cancel`);
  },

  async deleteEvent(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`${operatorBase(orgId)}/events/${eventId}`);
  },

  async openCheckIn(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/open-check-in`);
  },

  async closeCheckIn(orgId: string, eventId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/close-check-in`);
  },

  async startEvent(
    orgId: string,
    eventId: string,
    options?: PlayEventDrawGenerateInput
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/start`, options ?? {});
  },

  async regenerateFutureRounds(
    orgId: string,
    eventId: string,
    fromRoundNumber?: number
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/regenerate`, {
      ...(fromRoundNumber != null ? { fromRoundNumber } : {}),
    });
  },

  async generateRounds(
    orgId: string,
    eventId: string,
    format: PlayEventFormat,
    options?: PlayEventDrawGenerateInput,
    roundNumber = 1
  ): Promise<ApiResponse<null>> {
    const base = `${operatorBase(orgId)}/events/${eventId}/generate`;
    const body = options ?? {};
    if (format === 'social_rotate') {
      return apiPost<null>(`${base}/social-round`, { ...body, roundNumber });
    }
    if (format === 'round_robin') {
      return apiPost<null>(`${base}/round-robin`, body);
    }
    return apiPost<null>(`${base}/single-elimination`, body);
  },

  async reallocateRound(
    orgId: string,
    eventId: string,
    roundNumber: number
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/rounds/${roundNumber}/reallocate`
    );
  },

  async assignRoundParticipant(
    orgId: string,
    eventId: string,
    roundNumber: number,
    data: PlayEventRoundAssignInput
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/rounds/${roundNumber}/assign-participant`,
      data
    );
  },

  async removeRoundParticipant(
    orgId: string,
    eventId: string,
    roundNumber: number,
    data: PlayEventRoundRemoveInput
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/rounds/${roundNumber}/remove-participant`,
      data
    );
  },

  async listParticipants(
    orgId: string,
    eventId: string,
    filters?: {
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<PlayEventParticipant[]> | PaginatedResponse<PlayEventParticipant>> {
    return apiGet<PlayEventParticipant[]>(`${operatorBase(orgId)}/events/${eventId}/participants`, {
      params: filters,
    });
  },

  async getParticipantSummary(
    orgId: string,
    eventId: string
  ): Promise<ApiResponse<PlayEventParticipantSummary>> {
    return apiGet<PlayEventParticipantSummary>(
      `${operatorBase(orgId)}/events/${eventId}/participants/summary`
    );
  },

  async addParticipant(
    orgId: string,
    eventId: string,
    data: PlayEventParticipantAddInput,
    comped?: boolean
  ): Promise<ApiResponse<{ id: string; participantId?: string }>> {
    return apiPost<{ id: string; participantId?: string }>(
      `${operatorBase(orgId)}/events/${eventId}/participants`,
      comped ? { ...data, comped: true } : data
    );
  },

  async updateParticipant(
    orgId: string,
    eventId: string,
    participantId: string,
    data: PlayEventParticipantUpdateInput
  ): Promise<ApiResponse<PlayEventParticipant>> {
    return apiPatch<PlayEventParticipant>(
      `${operatorBase(orgId)}/events/${eventId}/participants/${participantId}`,
      data
    );
  },

  async swapRoundParticipants(
    orgId: string,
    eventId: string,
    roundNumber: number,
    data: { sourceParticipantId: string; targetParticipantId: string }
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/rounds/${roundNumber}/swap-participants`,
      data
    );
  },

  async assignMatchCourt(
    orgId: string,
    eventId: string,
    matchId: string,
    courtId: string | null
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`${operatorBase(orgId)}/events/${eventId}/matches/${matchId}/court`, {
      courtId,
    });
  },

  async recordDoorPayment(
    orgId: string,
    eventId: string,
    participantId: string,
    method: 'cash' | 'eftpos' | 'comped'
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/participants/${participantId}/door-payment`,
      { method }
    );
  },

  async checkInParticipant(
    orgId: string,
    eventId: string,
    participantId: string,
    options?: { prefersSingles?: boolean }
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/participants/${participantId}/check-in`,
      options ?? {}
    );
  },

  async removeParticipant(
    orgId: string,
    eventId: string,
    participantId: string
  ): Promise<ApiResponse<null>> {
    return apiDelete<null>(
      `${operatorBase(orgId)}/events/${eventId}/participants/${participantId}`
    );
  },

  async withdrawParticipant(
    orgId: string,
    eventId: string,
    participantId: string
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/participants/${participantId}/withdraw`,
      {}
    );
  },

  async kioskCheckIn(
    orgId: string,
    eventId: string,
    data: { contactEmail?: string; contactPhone?: string; prefersSingles?: boolean }
  ): Promise<ApiResponse<PlayEventParticipant>> {
    return apiPost<PlayEventParticipant>(`${operatorBase(orgId)}/events/${eventId}/kiosk/check-in`, data);
  },

  async addKioskParticipant(
    orgId: string,
    eventId: string,
    data: PlayEventParticipantAddInput & { checkIn?: boolean; comped?: boolean; prefersSingles?: boolean }
  ): Promise<ApiResponse<AddParticipantResult>> {
    return apiPost<AddParticipantResult>(`${operatorBase(orgId)}/events/${eventId}/participants/kiosk`, data);
  },

  async socialWithdraw(
    orgId: string,
    eventId: string,
    participantId: string,
    roundNumber: number
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/events/${eventId}/social-withdraw`, {
      participantId,
      roundNumber,
    });
  },

  async listMatches(orgId: string, eventId: string): Promise<ApiResponse<PlayEventMatch[]>> {
    return apiGet<PlayEventMatch[]>(`${operatorBase(orgId)}/events/${eventId}/matches`);
  },

  async recordScore(
    orgId: string,
    eventId: string,
    matchId: string,
    data: PlayEventScoreInput
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${operatorBase(orgId)}/events/${eventId}/matches/${matchId}/score`,
      data
    );
  },

  async listSeries(orgId: string): Promise<ApiResponse<PlayEventSeries[]>> {
    return apiGet<PlayEventSeries[]>(`${operatorBase(orgId)}/series`);
  },

  async getSeries(orgId: string, seriesId: string): Promise<ApiResponse<PlayEventSeries>> {
    return apiGet<PlayEventSeries>(`${operatorBase(orgId)}/series/${seriesId}`);
  },

  async createSeries(
    orgId: string,
    data: PlayEventSeriesCreateInput
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`${operatorBase(orgId)}/series`, data);
  },

  async updateSeries(
    orgId: string,
    seriesId: string,
    data: PlayEventSeriesUpdateInput
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`${operatorBase(orgId)}/series/${seriesId}`, data);
  },

  async deleteSeries(orgId: string, seriesId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`${operatorBase(orgId)}/series/${seriesId}`);
  },

  async cancelSeries(orgId: string, seriesId: string): Promise<ApiResponse<null>> {
    return apiPost<null>(`${operatorBase(orgId)}/series/${seriesId}/cancel`);
  },

  async generateSeriesUpcoming(
    orgId: string,
    seriesId: string
  ): Promise<
    ApiResponse<{
      createdEventIds: string[];
      skipped: Array<{ startsAt: string; reason: string }>;
    }>
  > {
    return apiPost(`${operatorBase(orgId)}/series/${seriesId}/generate-upcoming`, {});
  },

  async extendSeriesSchedule(
    orgId: string,
    seriesId: string
  ): Promise<
    ApiResponse<{
      createdEventIds: string[];
      skipped: Array<{ startsAt: string; reason: string }>;
    }>
  > {
    return apiPost(`${operatorBase(orgId)}/series/${seriesId}/extend-schedule`, {});
  },

  // ─── Public org ─────────────────────────────────────────────────────────

  async listPublicEvents(
    orgSlug: string,
    options?: { featured?: boolean }
  ): Promise<ApiResponse<PublicPlayEventSummary[]>> {
    const query = options?.featured ? '?featured=true' : '';
    return publicApiGet<PublicPlayEventSummary[]>(`${publicBase(orgSlug)}/events${query}`);
  },

  async getPublicEvent(
    orgSlug: string,
    eventSlug: string
  ): Promise<ApiResponse<PlayEventPublicDetailResponse>> {
    return publicApiGet<PlayEventPublicDetailResponse>(
      `${publicBase(orgSlug)}/events/slug/${encodeURIComponent(eventSlug)}`
    );
  },

  async getPublicEventBoard(
    orgSlug: string,
    eventSlug: string
  ): Promise<ApiResponse<PlayEventPublicBoardResponse>> {
    return publicApiGet<PlayEventPublicBoardResponse>(
      `${publicBase(orgSlug)}/events/slug/${encodeURIComponent(eventSlug)}/board`
    );
  },

  async joinPublicEvent(
    orgSlug: string,
    eventSlug: string,
    data: PlayEventJoinPayload
  ): Promise<ApiResponse<PlayEventJoinResponse>> {
    return apiPost<PlayEventJoinResponse>(
      `${publicBase(orgSlug)}/events/slug/${encodeURIComponent(eventSlug)}/join`,
      data
    );
  },

  async getMyMatches(
    orgSlug: string,
    eventId?: string
  ): Promise<ApiResponse<PlayEventMatch[]>> {
    return apiGet<PlayEventMatch[]>(`${publicBase(orgSlug)}/my-matches`, {
      params: eventId ? { eventId } : undefined,
    });
  },

  async getMyRegistrations(orgSlug: string): Promise<ApiResponse<MyPlayEventRegistration[]>> {
    return apiGet<MyPlayEventRegistration[]>(`${publicBase(orgSlug)}/my-registrations`);
  },

  async withdrawFromRound(
    orgSlug: string,
    eventSlug: string,
    data: PlayEventSocialWithdrawInput
  ): Promise<ApiResponse<null>> {
    return apiPost<null>(
      `${publicBase(orgSlug)}/events/slug/${encodeURIComponent(eventSlug)}/withdraw-round`,
      data
    );
  },
};
